/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect, useRef, useState } from 'react'
import { SaveIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiTableViewDropdownPanel, { toTableSaveViewApi } from 'components/Ui/UiTableViewDropdownPanel'
import { useTypedSelector } from 'helpers/reducers'
import { TableViewId } from 'types/enums/specific/TableViewId'
import { TableInitBase } from 'utils/hooks/useTable'
import { useAppDispatch } from 'helpers/store'
import { hydrateActiveTableViewFromSession } from 'utils/table/activeTableView'
import { toNumericViewId } from 'utils/table/tableViewId'

interface UiTableSaveViewButtonProps {
    tableInit: TableInitBase
    tableId: TableViewId
}

const UiTableSaveViewButton: React.FC<UiTableSaveViewButtonProps> = ({ tableInit, tableId }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [open, setOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const seller_id = toNumericViewId(useTypedSelector((state) => state.sessionState.session?.uid))
    const activeViewId = useTypedSelector((state) => state.tableViewsState.activeViewIds?.[tableId] ?? null)
    const hasLoaded = useTypedSelector((state) => state.tableViewsState.hasLoaded)

    const allViews = useTypedSelector((state) => state.tableViewsState.views)
    const views = allViews
        .filter((v) => v.table_id == tableId)
        .filter((v) => seller_id !== null && toNumericViewId(v.seller_id) === seller_id)

    const saveViewApi = toTableSaveViewApi(tableInit)

    useEffect(() => {
        hydrateActiveTableViewFromSession(dispatch, tableId, {
            views: allViews,
            sellerId: seller_id,
            hasLoaded,
        })
    }, [dispatch, tableId, allViews, seller_id, hasLoaded])

    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const activeView =
        activeViewId !== null
            ? views.find((v) => toNumericViewId(v.id) === activeViewId) ?? null
            : null

    return (
        <div ref={containerRef} className="UiTableSaveViewButton relative">
            <UiButton
                size={UiButtonSize.Normal}
                style={UiButtonStyle.Secondary}
                onClick={() => setOpen((v) => !v)}
                title={t('View') as string}
                icon={<SaveIcon className={clsx('w-4 h-4', activeView && 'text-blue-500')} />}
                className={clsx(
                    open && 'bg-gray-100 dark:bg-gray-800',
                    activeView && 'border-blue-300 dark:border-blue-600'
                )}
            />
            <UiTableViewDropdownPanel
                tableInit={saveViewApi}
                tableId={tableId}
                open={open}
                onClose={() => setOpen(false)}
            />
        </div>
    )
}

export default UiTableSaveViewButton
